<template>
    <ul role="menubar" aria-disabled="false" aria-label="Pagination"
        :class="['pagination', 'b-pagination', 'justify-content-center', { 'dark-mode': darkMode }]">
        <li role="presentation" :class="{ 'page-item': true, 'disabled': currentPage === 1 }" class="prev-item">
            <button @click="previousPage" role="menuitem" aria-label="Go to previous page"
                :aria-disabled="currentPage === 1" tabindex="0"
                class="page-link rounded-full focus:outline-none"
                :disabled="currentPage === 1">
                <svg xmlns="http://www.w3.org/2000/svg" width="18px" height="18px" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                    class="feather feather-chevron-left">
                    <polyline points="15 18 9 12 15 6"></polyline>
                </svg>
            </button>
        </li>
        <li v-if="showStartEllipsis" role="presentation" class="page-item">
            <button @click="goToPage(1)" role="menuitemradio" type="button" aria-label="Go to page 1"
                :aria-checked="1 === currentPage" tabindex="0"
                class="page-link rounded-full focus:outline-none">1</button>
        </li>
        <li v-if="showStartEllipsis" role="presentation" class="page-item disabled"><span
                class="page-link rounded-full">...</span></li>
        <li v-for="pageNumber in displayedPages" :key="pageNumber" role="presentation"
            :class="{ 'page-item': true, 'active': pageNumber === currentPage, '': pageNumber === currentPage }">
            <button @click="goToPage(pageNumber)" role="menuitemradio" type="button"
                :aria-label="`Go to page ${pageNumber}`" :aria-checked="pageNumber === currentPage"
                :aria-posinset="pageNumber" :aria-setsize="totalPages" tabindex="0"
                class="page-link rounded-full focus:outline-none">{{ pageNumber }}</button>
        </li>
        <li v-if="showEndEllipsis" role="presentation" class="page-item disabled"><span
                class="page-link rounded-full">...</span></li>
        <li v-if="showEndEllipsis" role="presentation" class="page-item">
            <button @click="goToPage(totalPages)" role="menuitemradio" type="button"
                :aria-label="`Go to page ${totalPages}`" :aria-checked="totalPages === currentPage"
                :aria-posinset="totalPages" :aria-setsize="totalPages" tabindex="0"
                class="page-link rounded-full focus:outline-none">{{ totalPages }}</button>
        </li>
        <li role="presentation" :class="{ 'page-item': true, 'disabled': currentPage === totalPages }"
            class="next-item">
            <button @click="nextPage" role="menuitem" aria-label="Go to next page"
                :aria-disabled="currentPage === totalPages" tabindex="0"
                class="page-link rounded-full focus:outline-none"
                :disabled="currentPage === totalPages">
                <svg xmlns="http://www.w3.org/2000/svg" width="18px" height="18px" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                    class="feather feather-chevron-right">
                    <polyline points="9 18 15 12 9 6"></polyline>
                </svg>
            </button>
        </li>
    </ul>
</template>

<script>
export default {
    props: {
        totalItems: {
            type: Number,
            required: true
        },
        itemsPerPage: {
            type: Number,
            default: 10
        },
        currentPage: {
            type: Number,
            required: true
        },
        maxDisplayedPages: {
            type: Number,
            default: 5 // Quantidade máxima de botões numerados a serem exibidos
        },
        darkMode: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        totalPages() {
            return Math.ceil(this.totalItems / this.itemsPerPage);
        },
        displayedPages() {
            const pages = [];
            let start = 1;
            let end = this.totalPages;
            if (this.totalPages > this.maxDisplayedPages) {
                const mid = Math.ceil(this.maxDisplayedPages / 2);
                start = Math.max(1, this.currentPage - mid);
                end = Math.min(this.totalPages, this.currentPage + mid);
                if (this.currentPage <= mid) {
                    end = this.maxDisplayedPages;
                } else if (this.currentPage >= this.totalPages - mid) {
                    start = this.totalPages - this.maxDisplayedPages + 1;
                }
            }
            for (let i = start; i <= end; i++) {
                pages.push(i);
            }
            return pages;
        },
        showStartEllipsis() {
            return this.totalPages > this.maxDisplayedPages && this.currentPage > Math.ceil(this.maxDisplayedPages / 2) + 1;
        },
        showEndEllipsis() {
            return this.totalPages > this.maxDisplayedPages && this.currentPage < this.totalPages - Math.ceil(this.maxDisplayedPages / 2);
        }
    },
    methods: {
        nextPage() {
            if (this.currentPage < this.totalPages) {
                this.$emit('pageChanged', this.currentPage + 1);
            }
        },
        previousPage() {
            if (this.currentPage > 1) {
                this.$emit('pageChanged', this.currentPage - 1);
            }
        },
        goToPage(pageNumber) {
            this.$emit('pageChanged', pageNumber);
        }
    }
};
</script>

<style scoped>
.pagination {
    margin: 2rem 0;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.6rem;
    list-style: none;
}

.page-item {
    list-style: none;
}

.page-link {
    width: 2.5rem;
    height: 2.5rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 50% !important;
    border: none;
    background: #f9f7ff;
    color: #5e5873;
    box-shadow: 0 2px 8px rgba(115, 103, 240, 0.08);
    padding: 0 !important;
    transition: box-shadow 0.2s ease, background 0.2s ease, color 0.2s ease;
}

.page-item.disabled .page-link {
    opacity: 0.6;
    box-shadow: none;
}

.page-item.active .page-link {
    background: #7367f0 !important;
    color: #fff !important;
    box-shadow: 0 6px 18px rgba(115, 103, 240, 0.25);
}

.page-item:hover .page-link:not(.disabled) {
    box-shadow: 0 4px 12px rgba(115, 103, 240, 0.15);
}

.page-item svg {
    pointer-events: none;
}
</style>
